import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Users, Plus, Phone, UserCheck, UserX, X, AlertCircle, CheckCircle, Archive, RotateCcw, Edit2, Search, Download } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import { downloadCSV } from '../../utils/csvExport';
import Modal from '../../components/Modal';

const API = process.env.REACT_APP_BACKEND_URL;

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3000); return () => clearTimeout(t); }, [onClose]);
  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-body animate-fade-in-up
      ${type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
      {message}
    </div>
  );
}

const EMPTY_FORM = { name: '', phone: '', batch_id: '' };

export default function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editStudent, setEditStudent] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [resetPasswordChecked, setResetPasswordChecked] = useState(false);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);

  const fetchAll = async () => {
    try {
      const [studRes, batRes] = await Promise.all([
        axios.get(`${API}/api/admin/students`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/admin/batches`, { headers: getAuthHeaders() }),
      ]);
      setStudents(studRes.data);
      setBatches(batRes.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchAll(); }, []);

  const batchName = (id) => batches.find(b => b.id === id)?.name || null;

  // Filtering
  const filtered = students
    .filter(s => (showArchived ? s.is_archived : !s.is_archived))
    .filter(s => !searchQuery || s.name.toLowerCase().includes(searchQuery.toLowerCase()))
    .filter(s => !batchFilter || s.batch_id === batchFilter);

  const activeCount = students.filter(s => !s.is_archived).length;
  const archivedCount = students.filter(s => s.is_archived).length;

  const openAdd = () => { setEditStudent(null); setForm(EMPTY_FORM); setResetPasswordChecked(false); setShowForm(true); };
  const openEdit = (s) => { setEditStudent(s); setForm({ name: s.name, phone: s.phone, batch_id: s.batch_id || '' }); setResetPasswordChecked(false); setShowSaveConfirm(false); setShowForm(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!editStudent && (!form.name.trim() || !form.phone.trim())) {
      setToast({ message: 'Please fill in name and phone number.', type: 'error' }); return;
    }
    if (editStudent && !form.name.trim() && !form.phone.trim() && form.batch_id === undefined) {
      setToast({ message: 'Nothing to update.', type: 'error' }); return;
    }
    // Change 1: if checkbox is checked, show confirmation before saving
    if (editStudent && resetPasswordChecked) {
      setShowSaveConfirm(true);
      return;
    }
    setSubmitting(true);
    try {
      if (editStudent) {
        const payload = {};
        if (form.name.trim()) payload.name = form.name.trim();
        if (form.phone.trim()) payload.phone = form.phone.trim();
        payload.batch_id = form.batch_id || null;
        // BUG FIX (Change 2): removed form.password.trim() — password was removed from
        // EMPTY_FORM but still referenced here, causing TypeError on every edit save
        await axios.put(`${API}/api/admin/students/${editStudent.id}`, payload, { headers: getAuthHeaders() });
        setToast({ message: 'Student details updated successfully.', type: 'success' });
      } else {
        await axios.post(`${API}/api/admin/students`,
          { name: form.name.trim(), phone: form.phone.trim(), batch_id: form.batch_id || null },
          { headers: getAuthHeaders() });
        setToast({ message: `${form.name} added! Default password is their phone number.`, type: 'success' });
      }
      setShowForm(false);
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Something went wrong.', type: 'error' });
    } finally { setSubmitting(false); }
  };

  const handleArchive = async (id, name) => {
    if (!window.confirm(`Archive ${name}? They won't be able to login. Data is kept.`)) return;
    try {
      await axios.patch(`${API}/api/admin/students/${id}/archive`, {}, { headers: getAuthHeaders() });
      setToast({ message: `${name} archived.`, type: 'success' });
      fetchAll();
    } catch { setToast({ message: 'Could not archive. Try again.', type: 'error' }); }
  };

  const handleUnarchive = async (id, name) => {
    try {
      await axios.patch(`${API}/api/admin/students/${id}/unarchive`, {}, { headers: getAuthHeaders() });
      setToast({ message: `${name} reactivated!`, type: 'success' });
      fetchAll();
    } catch { setToast({ message: 'Could not reactivate. Try again.', type: 'error' }); }
  };

  const handleConfirmSaveWithReset = async () => {
    if (!editStudent) return;
    setSubmitting(true);
    try {
      // 1. Save updated student details
      const payload = {};
      if (form.name.trim()) payload.name = form.name.trim();
      if (form.phone.trim()) payload.phone = form.phone.trim();
      payload.batch_id = form.batch_id || null;
      await axios.put(`${API}/api/admin/students/${editStudent.id}`, payload, { headers: getAuthHeaders() });
      // 2. Reset password to registered phone number
      await axios.post(`${API}/api/admin/students/${editStudent.id}/reset-password`, {}, { headers: getAuthHeaders() });
      setToast({ message: "Student details updated successfully. Password has been reset to the student's registered phone number.", type: 'success' });
      setShowSaveConfirm(false);
      setResetPasswordChecked(false);
      setShowForm(false);
      fetchAll();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Something went wrong.', type: 'error' });
    } finally { setSubmitting(false); }
  };

  const handleExportStudents = () => {
    const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const d = new Date();
    const data = students.map(s => ({
      'Name': s.name,
      'Phone Number': s.phone,
      'Batch': batches.find(b => b.id === s.batch_id)?.name || 'Unassigned',
      'Status': s.is_archived ? 'Archived' : 'Active',
      'Joined': s.created_at ? new Date(s.created_at).toLocaleDateString('en-IN') : '—',
    }));
    downloadCSV(data, `students_${MONTHS[d.getMonth()]}_${d.getFullYear()}.csv`);
  };

  return (
    <div className="space-y-5 animate-fade-in-up">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">Manage</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">Students</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            data-testid="export-students-csv"
            onClick={handleExportStudents}
            disabled={students.length === 0}
            className="flex items-center gap-2 border-2 border-[#E7E5E4] hover:border-[#F59E0B] text-[#57534E] hover:text-[#D97706] rounded-full px-4 py-2.5 text-sm font-heading font-medium transition-all disabled:opacity-40">
            <Download className="w-4 h-4" strokeWidth={1.5} /> Export CSV
          </button>
          <button data-testid="add-student-button" onClick={openAdd}
            className="flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-5 py-2.5 text-sm font-heading font-medium shadow-button hover:-translate-y-0.5 transition-all">
            <Plus className="w-4 h-4" strokeWidth={2} /> Add Student
          </button>
        </div>
      </div>

      {/* Search + Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A8A29E]" strokeWidth={1.5} />
          <input
            data-testid="student-search-input"
            type="text" placeholder="Search by name..."
            value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            className="bg-white border-2 border-[#E7E5E4] rounded-2xl pl-10 pr-4 py-2.5 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm"
          />
        </div>
        <select
          data-testid="batch-filter-select"
          value={batchFilter} onChange={e => setBatchFilter(e.target.value)}
          className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-2.5 focus:border-[#F59E0B] outline-none text-[#292524] font-body text-sm min-w-36"
        >
          <option value="">All Batches</option>
          {batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-2 bg-white rounded-2xl p-1.5 shadow-card border border-[#E7E5E4]/50 w-fit">
        <button data-testid="tab-active-students" onClick={() => setShowArchived(false)}
          className={`px-5 py-2 rounded-xl text-sm font-heading font-medium transition-all ${!showArchived ? 'bg-[#F59E0B] text-white shadow-sm' : 'text-[#57534E] hover:text-[#D97706]'}`}>
          Active ({activeCount})
        </button>
        <button data-testid="tab-archived-students" onClick={() => setShowArchived(true)}
          className={`px-5 py-2 rounded-xl text-sm font-heading font-medium transition-all ${showArchived ? 'bg-[#57534E] text-white shadow-sm' : 'text-[#57534E] hover:text-[#D97706]'}`}>
          Archived ({archivedCount})
        </button>
      </div>

      {showForm && (
        <Modal
          title={editStudent ? 'Edit Student' : 'Add New Student'}
          onClose={() => { setShowForm(false); setResetPasswordChecked(false); setShowSaveConfirm(false); }}
          footer={
            <div className="flex gap-3">
              <button type="button" onClick={() => { setShowForm(false); setResetPasswordChecked(false); setShowSaveConfirm(false); }}
                className="flex-1 border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 font-heading font-medium text-sm hover:border-[#F59E0B] transition-colors">Cancel</button>
              <button data-testid="submit-add-student" type="submit" form="student-form" disabled={submitting}
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-heading font-medium text-sm shadow-button disabled:opacity-60 transition-all">
                {submitting ? 'Saving...' : editStudent ? 'Save Changes' : 'Add Student'}
              </button>
            </div>
          }
        >
          <form id="student-form" onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Full Name {!editStudent && <span className="text-red-400">*</span>}</label>
              <input data-testid="student-name-input" type="text" placeholder="Enter student's name"
                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body" />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Phone Number {!editStudent && <span className="text-red-400">*</span>}</label>
              <input data-testid="student-phone-input" type="tel" placeholder="Enter phone number"
                value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body" />
              {!editStudent && (
                <p className="text-xs text-[#A8A29E] font-body mt-1.5">Default password will be set to the phone number.</p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-[#57534E] mb-2">Batch (optional)</label>
              <select data-testid="student-batch-select" value={form.batch_id}
                onChange={e => setForm(f => ({ ...f, batch_id: e.target.value }))}
                className="bg-white border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body">
                <option value="">No batch assigned</option>
                {batches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
          </form>

          {/* ── Password Management checkbox (edit mode only) ── */}
          {editStudent && (
            <div className="mt-5 pt-5 border-t border-[#E7E5E4]/60">
              <p className="text-sm font-semibold text-[#292524] font-heading mb-3">Password Management</p>
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  data-testid="reset-password-checkbox"
                  type="checkbox"
                  checked={resetPasswordChecked}
                  onChange={e => setResetPasswordChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#F59E0B] cursor-pointer shrink-0"
                />
                <div>
                  <span className="text-sm text-[#292524] font-body">Reset Password to Default (Phone Number)</span>
                  <p className="text-xs text-[#A8A29E] font-body mt-1 leading-relaxed">
                    If selected, the student's password will be reset to their registered phone number when the changes are saved.
                  </p>
                </div>
              </label>
            </div>
          )}
        </Modal>
      )}

      {/* ── Confirmation overlay: shown when checkbox is checked and Save Changes is clicked ── */}
      {showSaveConfirm && editStudent && createPortal(
        <div
          onClick={(e) => e.target === e.currentTarget && setShowSaveConfirm(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', backgroundColor: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)' }}
        >
          <div className="bg-white rounded-3xl p-7 w-full max-w-sm shadow-2xl">
            <h2 className="font-heading text-lg font-semibold text-[#292524] mb-3">Reset Password</h2>
            <p className="text-sm text-[#57534E] font-body leading-relaxed mb-5">
              You are about to save the updated student details.
              <br /><br />
              Additionally, the student's password will be reset to their registered phone number because you selected this option.
              <br /><br />
              Do you want to continue?
            </p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowSaveConfirm(false)}
                className="flex-1 border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 font-heading font-medium text-sm hover:border-[#F59E0B] transition-colors">
                Cancel
              </button>
              <button type="button" data-testid="confirm-save-with-reset" onClick={handleConfirmSaveWithReset} disabled={submitting}
                className="flex-1 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 font-heading font-medium text-sm shadow-button disabled:opacity-60 transition-all">
                {submitting ? 'Saving...' : 'Confirm & Save'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Students list */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-white rounded-3xl p-12 text-center shadow-card border border-[#E7E5E4]/50">
          <div className="w-16 h-16 bg-[#FFEDD5] rounded-full flex items-center justify-center mx-auto mb-4">
            <Users className="w-8 h-8 text-[#F59E0B]" strokeWidth={1.5} />
          </div>
          <p className="font-heading text-lg font-medium text-[#292524]">
            {searchQuery || batchFilter ? 'No students match your filter' : showArchived ? 'No archived students' : 'No students yet'}
          </p>
          <p className="text-[#78716C] text-sm font-body mt-1">
            {searchQuery || batchFilter ? 'Try clearing the search or batch filter.' : 'Add your first student to get started!'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map(student => (
            <div key={student.id} data-testid="student-card"
              className={`bg-white rounded-3xl p-5 shadow-card border transition-all duration-300
                ${student.is_archived ? 'border-[#E7E5E4] opacity-75' : 'border-[#E7E5E4]/50 hover:shadow-card-hover'}`}>
              <div className="flex items-start gap-3">
                <div className={`w-11 h-11 rounded-2xl flex items-center justify-center font-heading font-semibold text-base shrink-0
                  ${student.is_archived ? 'bg-gray-100 text-gray-400' : 'bg-[#FFEDD5] text-[#D97706]'}`}>
                  {student.name?.[0]?.toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-heading font-medium text-[#292524] truncate">{student.name}</p>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <Phone className="w-3 h-3 text-[#A8A29E]" strokeWidth={1.5} />
                    <p className="text-xs text-[#78716C] font-body">{student.phone}</p>
                  </div>
                  {student.batch_id && batchName(student.batch_id) && (
                    <span className="inline-block mt-1.5 text-xs bg-[#FFEDD5] text-[#D97706] rounded-full px-2.5 py-0.5 font-body">
                      {batchName(student.batch_id)}
                    </span>
                  )}
                  <div className="flex items-center gap-1 mt-1">
                    {student.is_archived
                      ? <><UserX className="w-3 h-3 text-gray-400" strokeWidth={1.5} /><p className="text-xs text-gray-400 font-body">Archived</p></>
                      : <><UserCheck className="w-3 h-3 text-green-500" strokeWidth={1.5} /><p className="text-xs text-green-600 font-body">Active</p></>
                    }
                  </div>
                </div>
                <div className="flex flex-col gap-1 shrink-0">
                  {!student.is_archived && (
                    <button data-testid={`edit-student-${student.id}`} onClick={() => openEdit(student)}
                      title="Edit student" className="text-[#A8A29E] hover:text-[#F59E0B] transition-colors p-1">
                      <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  )}
                  {student.is_archived ? (
                    <button data-testid={`unarchive-student-${student.id}`} onClick={() => handleUnarchive(student.id, student.name)}
                      title="Reactivate" className="text-[#A8A29E] hover:text-green-500 transition-colors p-1">
                      <RotateCcw className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  ) : (
                    <button data-testid={`archive-student-${student.id}`} onClick={() => handleArchive(student.id, student.name)}
                      title="Archive" className="text-[#A8A29E] hover:text-amber-500 transition-colors p-1">
                      <Archive className="w-3.5 h-3.5" strokeWidth={1.5} />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
